/**
 * Datos de acceso del trabajador (T034; wiring §4.2 y §8.5): `GET /me` y `POST /privacy/accept`.
 *
 * Ambas son rutas **autenticadas**: el cliente añade la cookie `fermin_staff_session` y la cabecera
 * `X-Fermin-Device` firmada con `id=` (no se pasa `includeDeviceId`). Si el dispositivo no tiene
 * clave, no hay nada que consultar: las pantallas comprueban `hasKey()` antes de habilitar la query.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { StaffMeSchema, type StaffMe } from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';

import { setPrivacyRequired } from './privacy-store';

export const STAFF_ME_QUERY_KEY = ['staff-me'] as const;

export function fetchStaffMe(): Promise<StaffMe> {
  return api.get('/me', { schema: StaffMeSchema });
}

/** `GET /me`. Responde 200 también con empresa `SUSPENDED` o privacidad pendiente: enruta la UI. */
export function useStaffMe(options: { enabled?: boolean } = {}): UseQueryResult<StaffMe, ApiError> {
  return useQuery<StaffMe, ApiError>({
    queryKey: STAFF_ME_QUERY_KEY,
    queryFn: fetchStaffMe,
    enabled: options.enabled ?? true,
    retry: false,
  });
}

export function acceptPrivacy(version: string): Promise<undefined> {
  return api.post('/privacy/accept', { body: { version } });
}

/** Tras el 204 se apaga el flag del 428 y se refresca `GET /me` (`privacy_notice.pending`). */
export function useAcceptPrivacy(): UseMutationResult<undefined, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<undefined, ApiError, string>({
    mutationFn: acceptPrivacy,
    onSuccess: async () => {
      setPrivacyRequired(false);
      await queryClient.invalidateQueries({ queryKey: STAFF_ME_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /session/logout (T049)
// ---------------------------------------------------------------------------

/**
 * Cierra la sesión de este dispositivo (`204`). El dispositivo sigue `ACTIVE` en la API.
 * `notifyErrors: false` (006, Perfil): un `423`/`401` no emite el evento global de navegación.
 */
export function logoutStaffSession(options: { notifyErrors?: boolean } = {}): Promise<undefined> {
  return api.post('/session/logout', options);
}

/**
 * La usa la pantalla de cuenta inactiva: se limpia la caché pase lo que pase, porque un `403`
 * `WORKER_INACTIVE` o `401` en el camino significa que esa sesión ya no sirve de todos modos.
 */
export function useStaffLogout(): UseMutationResult<undefined, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<undefined, ApiError, void>({
    mutationFn: () => logoutStaffSession(),
    onSettled: () => {
      queryClient.clear();
    },
  });
}
